import { isTrunk, type OrgChart } from '@/modules/org-chart/types';

/**
 * Depth-first parent → children, in the charts' own (creation) order. A
 * readable chart whose direct parent is absent from `charts` — because it is
 * unreadable, or does not exist — renders as its own top-level row rather
 * than disclosing that a parent exists.
 */
export function orgChartTreeRows(charts: OrgChart[]): { chart: OrgChart; depth: number }[] {
  const ids = new Set(charts.map((c) => c.id));
  const byParent = new Map<string | null, OrgChart[]>();
  for (const c of charts) {
    const key = c.parentChartId && ids.has(c.parentChartId) ? c.parentChartId : null;
    byParent.set(key, [...(byParent.get(key) ?? []), c]);
  }
  const out: { chart: OrgChart; depth: number }[] = [];
  const visit = (parentKey: string | null, depth: number) => {
    for (const c of byParent.get(parentKey) ?? []) {
      out.push({ chart: c, depth });
      visit(c.id, depth + 1);
    }
  };
  visit(null, 0);
  return out;
}

/**
 * The trunk first, then every other readable chart alphabetically — parentage
 * is still on each `OrgChart` for lifecycle and fallback logic, but nothing in
 * the UI groups charts by it any more.
 */
export function sortOrgCharts(charts: OrgChart[]): OrgChart[] {
  return [...charts].sort((a, b) => {
    if (isTrunk(a) !== isTrunk(b)) return isTrunk(a) ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}
